import type { FsrsKarte, Rating } from '../types'

const W = [
  0.4072, 1.1829, 3.1262, 15.4722,
  7.2102, 0.5316, 1.0651, 0.0589,
  1.5330, 0.1544, 1.0070, 1.9395,
  0.1100, 0.2900, 2.2700, 0.2500,
  2.9898, 0.5100, 0.3400,
]
const REQUEST_RETENTION = 0.9
const MAX_INTERVAL      = 36500
const DECAY  = -0.5
const FACTOR = Math.pow(0.9, 1 / DECAY) - 1

function retrievability(stability: number, elapsedDays: number): number {
  return Math.pow(1 + FACTOR * elapsedDays / stability, DECAY)
}
function nextStabilityRecall(d: number, s: number, r: number, rating: Rating): number {
  const hardPenalty = rating === 2 ? W[15] : 1
  const easyBonus   = rating === 4 ? W[16] : 1
  return s * (
    Math.exp(W[8]) * (11 - d) *
    Math.pow(s, -W[9]) *
    (Math.exp((1 - r) * W[10]) - 1) *
    hardPenalty * easyBonus + 1
  )
}
function nextStabilityForgot(d: number, s: number, r: number): number {
  return W[11] * Math.pow(d, -W[12]) * (Math.pow(s + 1, W[13]) - 1) * Math.exp((1 - r) * W[14])
}
function nextDifficulty(d: number, rating: Rating): number {
  const delta    = W[6] * (rating - 3)
  const init4    = W[4] - Math.exp(W[5] * 3) + 1
  const updated  = d - delta
  const reverted = updated + W[7] * (init4 - updated)
  return Math.min(10, Math.max(1, reverted))
}
function calcInterval(stability: number): number {
  const interval = (stability / FACTOR) * (Math.pow(REQUEST_RETENTION, 1 / DECAY) - 1)
  return Math.min(MAX_INTERVAL, Math.max(1, Math.round(interval)))
}

export function neueKarte(uebungsabschnittId: string): FsrsKarte {
  return {
    id: uebungsabschnittId,
    uebungsabschnittId,
    due: Date.now(),
    stability: 0,
    difficulty: 5,
    reps: 0,
    lapses: 0,
    state: 'new',
  }
}

export function bewerteKarte(karte: FsrsKarte, rating: Rating): FsrsKarte {
  const now     = Date.now()
  const elapsed = karte.lastReview ? (now - karte.lastReview) / 86_400_000 : 0
  let { stability, difficulty, reps, lapses, state } = karte
  let intervalDays: number

  if (state === 'new') {
    stability  = W[rating - 1]
    difficulty = W[4] - Math.exp(W[5] * (rating - 1)) + 1
    if (rating <= 3) { intervalDays = 1; state = 'learning' }
    else             { intervalDays = calcInterval(stability); state = 'review' }
    reps = 1
  } else if (state === 'learning' || state === 'relearning') {
    stability = stability * Math.exp(W[17] * (rating - 3 + W[18]))
    if (rating === 1)       intervalDays = 1
    else if (rating <= 3) { intervalDays = 1; state = 'review' }
    else                  { intervalDays = calcInterval(stability); state = 'review' }
    reps += 1
  } else {
    const r    = retrievability(stability, elapsed)
    difficulty = nextDifficulty(difficulty, rating)
    if (rating === 1) {
      stability    = nextStabilityForgot(difficulty, stability, r)
      lapses      += 1
      intervalDays = 1
      state        = 'relearning'
    } else {
      stability    = nextStabilityRecall(difficulty, stability, r, rating)
      intervalDays = calcInterval(stability)
      reps        += 1
    }
  }

  return {
    id: karte.id,
    uebungsabschnittId: karte.uebungsabschnittId,
    due: now + intervalDays * 86_400_000,
    stability,
    difficulty,
    reps,
    lapses,
    state,
    lastReview: now,
  }
}

export function istFaellig(karte: FsrsKarte): boolean {
  return karte.due <= Date.now()
}

export function tageBisFaellig(karte: FsrsKarte): number {
  return Math.max(0, Math.round((karte.due - Date.now()) / 86_400_000))
}

export function masteryWert(karte: FsrsKarte): number {
  if (karte.state === 'new' || karte.reps === 0) return 0
  if (karte.state === 'learning')   return 0.2
  if (karte.state === 'relearning') return 0.25
  const s = karte.stability
  if (s <  1)  return 0.3
  if (s <  3)  return 0.45
  if (s <  7)  return 0.6
  if (s < 21)  return 0.75
  if (s < 60)  return 0.88
  return 1.0
}
